/**
 * consoleTail — module-level rolling buffer of `console.error` and
 * `console.warn` lines captured for the lifetime of the browser tab.
 *
 * Why a module singleton: the StatusPage reads the buffer to surface
 * recent JS issues + ship them with the "Send to support" report. The
 * tab's main app (the DAW) is where errors usually originate, not the
 * StatusPage itself — so we MUST install the interceptor at app boot,
 * not inside the StatusPage component.
 *
 * Safety:
 *  - Buffer is capped at MAX entries (oldest evicted FIFO).
 *  - Each captured arg is stringified with a length cap to avoid
 *    accidentally embedding a huge object dump (e.g. a Buffer).
 *  - The original `console.error/warn` are STILL called via `Reflect.apply`
 *    so devtools behaviour is unchanged.
 *  - Install is idempotent — calling twice is a no-op.
 *
 *  Public API:
 *    installConsoleTail()  — call once at app boot. Returns the singleton.
 *    getConsoleTail()      — returns a frozen copy of the buffer (newest last).
 *    clearConsoleTail()    — empties the buffer (used by the StatusPage clear btn).
 */

const MAX = 20;        // rolling window size
const ARG_CAP = 240;   // chars per stringified arg
const buffer = [];     // module-level singleton; survives page navigation
let installed = false;

function safeStringify(arg) {
  if (arg == null) return String(arg);
  const t = typeof arg;
  if (t === "string") return arg.length > ARG_CAP ? arg.slice(0, ARG_CAP) + "…" : arg;
  if (t === "number" || t === "boolean") return String(arg);
  if (arg instanceof Error) {
    const head = `${arg.name}: ${arg.message}`;
    return head.length > ARG_CAP ? head.slice(0, ARG_CAP) + "…" : head;
  }
  try {
    const s = JSON.stringify(arg);
    return s && s.length > ARG_CAP ? s.slice(0, ARG_CAP) + "…" : (s || String(arg));
  } catch (_e) { void _e; return "[unstringifiable]"; }
}

function push(level, args) {
  // Skip our own ringback if any future code in this module logs.
  if (args && args[0] === "[consoleTail]") return;
  const text = Array.from(args).map(safeStringify).join(" ");
  // Ignore well-known noise that adds zero diagnostic value:
  //  - The autoplay-policy warning is emitted by Chromium every time an
  //    AudioContext is constructed before a user gesture. We intentionally
  //    create the engine at app boot so the user can see meters immediately;
  //    the ctx resumes on first transport play. Suppressing this warning
  //    keeps /status's "console errors" count clean.
  if (level === "warn" && /AudioContext was not allowed to start/i.test(text)) return;
  buffer.push({ level, text, ts: Date.now() });
  // FIFO evict so the buffer stays at MAX even under a hot loop.
  while (buffer.length > MAX) buffer.shift();
}

export function installConsoleTail() {
  if (installed) return;
  installed = true;
  if (typeof window === "undefined" || !window.console) return;
  const origError = console.error.bind(console);
  const origWarn  = console.warn.bind(console);
  // Replace with wrappers that capture then delegate.
  console.error = function consoleErrorTap(...args) {
    try { push("error", args); } catch (e) { void e; }
    Reflect.apply(origError, console, args);
  };
  console.warn = function consoleWarnTap(...args) {
    try { push("warn", args); } catch (e) { void e; }
    Reflect.apply(origWarn, console, args);
  };
  // Also surface uncaught errors + unhandled promise rejections, which
  // normally bypass console.error in some browsers.
  window.addEventListener("error", (ev) => {
    try { push("error", [`window.error: ${ev?.message || ""}`, ev?.filename, ev?.lineno]); } catch (e) { void e; }
  });
  window.addEventListener("unhandledrejection", (ev) => {
    try { push("error", [`unhandledrejection: ${ev?.reason?.message || ev?.reason || ""}`]); } catch (e) { void e; }
  });
  // Expose getters on window so the /status diagnostic page AND automated
  // tests (Playwright) can assert that noise-filters are working at runtime.
  try {
    window.getConsoleTail = getConsoleTail;
    window.clearConsoleTail = clearConsoleTail;
  } catch (e) { void e; }
}

export function getConsoleTail() {
  // Return a SHALLOW copy so consumers can't mutate the buffer in place.
  return buffer.slice();
}

export function clearConsoleTail() {
  buffer.length = 0;
}
